/* istanbul ignore file */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFormContext } from 'react-hook-form';
import { ChevronDownIcon, ChevronUpIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spacer,
  Text,
} from '@chakra-ui/react';

import RenderOptionList from 'components/fields/custom-fields/drop-down-group/single-select/RenderOptionList';
import { FieldWrapper } from 'components/fields/FieldWrapper';
import { recursiveSearch } from 'utils/recursiveSearch';

interface Props {
  name: string;
  label?: string;
  required?: boolean;
  placeholder: string;
  options: any[];
  onChange: (data: any) => void;
  defaultValue?: string;
  isReadOnly?: boolean;
  width?: number | string;
  isDisabled?: boolean;
  actionButtons?: {
    label: string;
    onClick: () => void;
    icon?: React.ElementType;
    iconColor?: string;
  }[];
}

const SmartRouteNextStepDropDown = (props: Props) => {
  const {
    name,
    label,
    options,
    required = false,
    onChange,
    defaultValue,
    placeholder = '',
    isReadOnly = false,
    width = 300,
    isDisabled = false,
    actionButtons = [],
  } = props;

  const { control, clearErrors, setValue } = useFormContext();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fieldValue, setFieldValue] = useState<string>(defaultValue || '');
  const [searchStr, setSearchStr] = useState('');
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const selectedObject = useCallback(recursiveSearch, []);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const scrollParentsRef = useRef<HTMLElement[]>([]);

  const handleClickOutside = useCallback((event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = (triggerRef.current as any).getBoundingClientRect();
    const { borderBottomWidth } = window.getComputedStyle(triggerRef.current as any);
    const bottomBorder = parseFloat(borderBottomWidth || '0') || 0;
    const dropdownHeight = 450; // keep in sync with container maxH
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top = rect.bottom - bottomBorder;
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      top = Math.max(0, rect.top - Math.min(dropdownHeight, spaceAbove));
    }

    setDropdownPosition({ top, left: rect.left, width: rect.width });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, updateDropdownPosition]);

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && fieldValue && optionsContainerRef.current) {
      const timeoutId = setTimeout(() => {
        const selectedElement = optionsContainerRef.current.querySelector(
          `[data-value="${fieldValue}"]`,
        );
        if (selectedElement) {
          (selectedElement as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen, fieldValue]);

  const getScrollParents = (node: HTMLElement | null): HTMLElement[] => {
    const parents: HTMLElement[] = [];
    if (!node) return parents;
    let parent: HTMLElement | null = node.parentElement;
    while (parent) {
      const { overflowY, overflow } = window.getComputedStyle(parent);
      const isScrollableY =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight;
      const isScrollable =
        isScrollableY ||
        ((overflow === 'auto' || overflow === 'scroll') &&
          parent.scrollHeight > parent.clientHeight);
      if (isScrollable) parents.push(parent);
      parent = parent.parentElement;
    }
    return parents;
  };

  useEffect(() => {
    if (!isOpen) return;

    const onScrollOrResize = (): void => {
      updateDropdownPosition();
    };

    // Attach to window resize and scroll
    window.addEventListener('resize', onScrollOrResize, {
      passive: true,
    } as AddEventListenerOptions);
    window.addEventListener('scroll', onScrollOrResize, {
      passive: true,
    } as AddEventListenerOptions);

    // Attach to scrollable ancestors to react to container scrolls
    const parents = getScrollParents(triggerRef.current as any);
    scrollParentsRef.current = parents;
    parents.forEach((el) => {
      el.addEventListener('scroll', onScrollOrResize, { passive: true });
    });

    function cleanup() {
      window.removeEventListener('resize', onScrollOrResize as any);
      window.removeEventListener('scroll', onScrollOrResize as any);
      scrollParentsRef.current.forEach((el) => {
        el.removeEventListener('scroll', onScrollOrResize as any);
      });
      scrollParentsRef.current = [];
    }

    // eslint-disable-next-line
    return cleanup;
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (defaultValue && fieldValue !== defaultValue) {
      setFieldValue(defaultValue);
      return;
    }

    const exists = selectedObject(options, 'value', fieldValue);
    if (!exists && fieldValue !== '') {
      setFieldValue('');
      setValue(name, '', { shouldValidate: false });
    }
  }, [defaultValue, options, fieldValue, name, selectedObject, setValue]);

  const filterValues = (acc: any[], curr: any) => {
    if (curr.children) {
      const children = curr.children.reduce(filterValues, []);
      if (children?.length) {
        return [...acc, { ...curr, children }];
      }
      return acc;
    }
    const fieldLabel = curr.label.toLowerCase();
    if (fieldLabel.includes(searchStr.toLowerCase())) {
      return [...acc, curr];
    }
    return acc;
  };

  const filterList = useCallback(() => {
    return options.reduce(filterValues, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, searchStr]);

  /**
   * To get the placeholder text
   */
  const getPlaceholderText = () => {
    if (fieldValue === '') return placeholder ?? 'Select Option';
    const selectedOption = selectedObject(options, 'value', fieldValue);
    if (!selectedOption) return placeholder ?? 'Select Option';
    return selectedOption?.label;
  };

  const handleSelection = (val: string) => {
    if (isReadOnly) return;
    setFieldValue(val);
    const fullOption = selectedObject(options, 'value', val);
    onChange && onChange(fullOption);
    setValue(name, val, {
      shouldDirty: true,
      shouldValidate: false,
    });
    clearErrors(name);
    setIsOpen(false);
  };

  const renderOption = filterList();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ''}
      rules={{ required: required ? 'This field is required.' : false }}
      render={({ fieldState }) => {
        return (
          <FieldWrapper name={name} label={label} required={required} fieldState={fieldState}>
            <Box position="relative" width={width}>
              <Flex
                ref={triggerRef}
                border="1px solid"
                borderColor={fieldState.invalid ? 'red.500' : 'gray.500'}
                fontSize={14}
                px={3}
                py={3}
                h="48px"
                bg="white.base"
                borderRadius={8}
                onClick={() => {
                  if (isDisabled) return;
                  setIsOpen((prevState) => !prevState);
                }}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
              >
                <Text
                  whiteSpace="nowrap"
                  overflowX="hidden"
                  color={fieldValue === '' ? 'gray.400' : 'inherit'}
                >
                  {getPlaceholderText()}
                </Text>
                <Spacer />
                {isOpen ? <ChevronUpIcon boxSize={5} /> : <ChevronDownIcon boxSize={5} />}
              </Flex>

              {isOpen &&
                createPortal(
                  <Box
                    bg="white.base"
                    border="1px solid"
                    borderColor="gray.500"
                    borderRadius={8}
                    ref={ref}
                    position="fixed"
                    top={`${dropdownPosition.top}px`}
                    left={`${dropdownPosition.left}px`}
                    width={`${dropdownPosition.width}px`}
                    maxH="480px"
                    zIndex={9999}
                    boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                    overflow="hidden"
                  >
                    {/* Search Bar */}
                    <Box p={2} bg="white.base" position="sticky" top="0" zIndex={2}>
                      <InputGroup>
                        <InputLeftElement h="100%" pointerEvents="none">
                          <SearchIcon fontSize="13px" />
                        </InputLeftElement>
                        <Input
                          isDisabled={isDisabled}
                          placeholder="Search"
                          onChange={(e) => setSearchStr(e.target.value)}
                        />
                      </InputGroup>
                    </Box>

                    {/* Scrollable Options */}
                    <Box
                      ref={optionsContainerRef}
                      overflowY="auto"
                      flex="1"
                      maxH={`calc(480px - ${actionButtons.length > 0 ? '80px' : '0px'} - 76px)`}
                      minH="130px"
                      p={2}
                    >
                      {renderOption.length === 0 ? (
                        <Text color="gray.500" fontSize="sm" textAlign="center" py={4}>
                          No results found
                        </Text>
                      ) : (
                        <RenderOptionList
                          options={renderOption}
                          onChange={handleSelection}
                          value={fieldValue}
                          isTruncated
                        />
                      )}
                    </Box>

                    {actionButtons.length > 0 && (
                      <Box
                        bottom="0"
                        bg="white.base"
                        px={2}
                        py={2}
                        zIndex={2}
                        borderTop="1px solid"
                        borderColor="gray.200"
                      >
                        {actionButtons.map((button, index) => (
                          <Flex
                            key={index}
                            alignItems="center"
                            gap={2}
                            px={2}
                            py={1}
                            borderRadius="md"
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => {
                              button.onClick();
                              setIsOpen(false);
                            }}
                          >
                            {button.icon && (
                              <Icon as={button.icon} color={button.iconColor} boxSize={5} />
                            )}
                            <Text fontSize="sm">{button.label}</Text>
                          </Flex>
                        ))}
                      </Box>
                    )}
                  </Box>,
                  document.body,
                )}
            </Box>
          </FieldWrapper>
        );
      }}
    />
  );
};

export default SmartRouteNextStepDropDown;
